import { z } from "zod";
import { adminOperationsExtendedSchemaVersionSchema, adminOperationsPartnerSchemaVersionSchema, adminOperationsSchemaVersionSchema, adminAuthorizationModelVersionSchema, adminPermissionSchema, adminReasonCodeSchema, adminRoleSchema, adminSafeIdentifierSchema, adminStageSchema, adminUlidSchema, } from "./admin-operations-core.js";
export const adminOperatorRegistrySchema = z
    .object({
    schemaVersion: adminOperationsSchemaVersionSchema,
    authorizationModelVersion: adminAuthorizationModelVersionSchema,
    operatorId: z.string().trim().min(1).max(128),
    status: z.enum(["ACTIVE", "DISABLED"]),
    roles: z.array(adminRoleSchema).min(1).max(adminRoleSchema.options.length),
    stages: z.array(adminStageSchema).min(1).max(adminStageSchema.options.length),
    grantExpiresAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    version: z.number().int().positive(),
})
    .strict()
    .superRefine((record, context) => {
    if (new Set(record.roles).size !== record.roles.length)
        context.addIssue({ code: "custom", path: ["roles"], message: "Roles must be unique." });
    if (new Set(record.stages).size !== record.stages.length)
        context.addIssue({ code: "custom", path: ["stages"], message: "Stages must be unique." });
});
export const adminAccessRequestSchema = z
    .object({
    schemaVersion: adminOperationsExtendedSchemaVersionSchema,
    id: adminUlidSchema,
    change: z.enum(["GRANT", "DISABLE"]),
    operatorId: z.string().trim().min(1).max(128),
    roles: z.array(adminRoleSchema).max(adminRoleSchema.options.length),
    stages: z.array(adminStageSchema).max(adminStageSchema.options.length),
    reasonCode: adminReasonCodeSchema,
    state: z.enum(["PENDING_APPROVAL", "APPROVED", "APPLIED", "REJECTED", "FAILED"]),
    requestedBy: z.string().min(1).max(128),
    requestedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    approvedBy: z.string().min(1).max(128).optional(),
    approvedAt: z.string().datetime().optional(),
    appliedAt: z.string().datetime().optional(),
    expectedRegistryVersion: z.number().int().nonnegative(),
    approvalMode: z.enum(["DUAL_CONTROL", "FOUNDER_RECENT_MFA"]).optional(),
})
    .strict()
    .superRefine((request, context) => {
    if (request.requestedBy === request.operatorId)
        context.addIssue({
            code: "custom",
            path: ["requestedBy"],
            message: "Self changes are denied.",
        });
    if (request.approvedBy && request.approvedBy === request.operatorId)
        context.addIssue({
            code: "custom",
            path: ["approvedBy"],
            message: "Nobody approves a change to their own access.",
        });
    if (request.approvedBy &&
        request.approvedBy === request.requestedBy &&
        request.approvalMode !== "FOUNDER_RECENT_MFA")
        context.addIssue({
            code: "custom",
            path: ["approvedBy"],
            message: "A second operator is required.",
        });
    if (request.approvalMode === "FOUNDER_RECENT_MFA") {
        if (request.approvedBy !== request.requestedBy)
            context.addIssue({
                code: "custom",
                path: ["approvedBy"],
                message: "Founder recent-MFA changes are approved by their requester.",
            });
        // Owners (the founder and listed owners) invite partners and grant owners; an owner grant
        // also needs the operator on the reviewed owner list, which the service checks against its
        // release. The mode keeps its 2026-10-10 name, from when only the founder could.
        const roles = request.roles.join(",");
        // Support agents too, since 2026-10-21: one role, invited by an owner with recent MFA.
        if (request.change === "GRANT" &&
            roles !== "BUSINESS_PARTNER" &&
            !(roles === "SUPPORT_AGENT" && request.reasonCode === "SUPPORT_AGENT_INVITE") &&
            !(roles === "ADMIN" && request.reasonCode === "OWNER_ACCESS_GRANT"))
            context.addIssue({
                code: "custom",
                path: ["roles"],
                message: "Founder single-operator approval is limited to partners, support agents and listed owners.",
            });
    }
    if (request.change === "GRANT" && (request.roles.length === 0 || request.stages.length === 0))
        context.addIssue({
            code: "custom",
            path: ["roles"],
            message: "A grant requires roles and stages.",
        });
    if (request.change === "DISABLE" && (request.roles.length > 0 || request.stages.length > 0))
        context.addIssue({
            code: "custom",
            path: ["roles"],
            message: "A disable request cannot introduce roles or stages.",
        });
});
export const adminAccessRequestCommandSchema = z
    .object({
    operatorId: z.string().trim().min(1).max(128),
    change: z.enum(["GRANT", "DISABLE"]),
    roles: z.array(adminRoleSchema).max(adminRoleSchema.options.length),
    stages: z.array(adminStageSchema).max(adminStageSchema.options.length),
    reasonCode: adminReasonCodeSchema,
    expectedRegistryVersion: z.number().int().nonnegative(),
})
    .strict();
export const adminAccessRequestResponseSchema = z
    .object({ data: z.object({ request: adminAccessRequestSchema }).strict() })
    .strict();
export const adminBreakGlassEligiblePermissionSchema = adminPermissionSchema.exclude([
    "SESSION_READ",
    "OPERATOR_ACCESS_APPROVE",
    "GIFT_EXPORT_RECOVER",
]);
export const adminBreakGlassActivationSchema = z
    .object({
    schemaVersion: adminOperationsExtendedSchemaVersionSchema,
    id: adminUlidSchema,
    operatorId: z.string().min(1).max(128),
    incidentId: adminSafeIdentifierSchema,
    permissions: z.array(adminBreakGlassEligiblePermissionSchema).min(1).max(10),
    state: z.enum(["PENDING_APPROVAL", "ACTIVE", "EXPIRED", "REVOKED"]),
    requestedBy: z.string().min(1).max(128),
    approvedBy: z.string().min(1).max(128).optional(),
    requestedAt: z.string().datetime(),
    approvedAt: z.string().datetime().optional(),
    expiresAt: z.string().datetime(),
    revokedAt: z.string().datetime().optional(),
})
    .strict()
    .superRefine((activation, context) => {
    if (activation.operatorId !== activation.requestedBy)
        context.addIssue({
            code: "custom",
            path: ["requestedBy"],
            message: "Break-glass must be requested by its subject.",
        });
    if (activation.approvedBy && activation.approvedBy === activation.requestedBy)
        context.addIssue({
            code: "custom",
            path: ["approvedBy"],
            message: "A second operator is required.",
        });
    if (new Set(activation.permissions).size !== activation.permissions.length)
        context.addIssue({
            code: "custom",
            path: ["permissions"],
            message: "Permissions must be unique.",
        });
    if (Date.parse(activation.expiresAt) > Date.parse(activation.requestedAt) + 60 * 60 * 1_000)
        context.addIssue({
            code: "custom",
            path: ["expiresAt"],
            message: "Break-glass expires within one hour.",
        });
});
export const adminBreakGlassCommandSchema = z
    .object({
    incidentId: adminSafeIdentifierSchema,
    permissions: z.array(adminBreakGlassEligiblePermissionSchema).min(1).max(10),
})
    .strict();
export const adminBreakGlassResponseSchema = z
    .object({ data: z.object({ activation: adminBreakGlassActivationSchema }).strict() })
    .strict();
export const adminOperatorPersonSchema = z
    .object({
    operatorId: z.string().min(1).max(128),
    email: z.string().min(3).max(320),
    /** INVITED: hasn't finished the first sign-in. NEEDS_RESET: must reset their password. */
    signIn: z.enum(["INVITED", "ACTIVE", "NEEDS_RESET", "DISABLED"]),
})
    .strict();
export const adminAccessReviewResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsExtendedSchemaVersionSchema,
        operators: z.array(adminOperatorRegistrySchema).max(200),
        nextCursor: z.string().max(512).optional(),
        /** The reviewed owner list this release carries, so an owner can grant listed owners. */
        ownerList: z
            .object({
            founderOperatorId: z.string().min(1).max(128).nullable(),
            listedOperatorIds: z.array(z.string().min(1).max(128)).max(20),
        })
            .strict()
            .optional(),
        /**
         * Who each operator is, from the operator sign-in pool, so the console shows people by
         * email and says whether they have accepted their invitation (admin audit, 2026-10-15).
         * Missing when the pool can't be read: the console then falls back to short ids.
         */
        people: z.array(adminOperatorPersonSchema).max(200).optional(),
    })
        .strict(),
})
    .strict();
/** The roles an owner invites and removes from People & access (support agents: 2026-10-21). */
export const adminInvitedRoleSchema = z.enum(["BUSINESS_PARTNER", "SUPPORT_AGENT"]);
const invitePhrases = {
    BUSINESS_PARTNER: "INVITE BUSINESS PARTNER",
    SUPPORT_AGENT: "INVITE SUPPORT AGENT",
};
const disablePhrases = {
    BUSINESS_PARTNER: "DISABLE BUSINESS PARTNER",
    SUPPORT_AGENT: "REMOVE SUPPORT AGENT",
};
export const adminBusinessPartnerInviteCommandSchema = z
    .object({
    email: z.string().trim().toLowerCase().email().max(254),
    role: adminInvitedRoleSchema.default("BUSINESS_PARTNER"),
    confirmationPhrase: z.enum([invitePhrases.BUSINESS_PARTNER, invitePhrases.SUPPORT_AGENT]),
})
    .strict()
    .refine((command) => command.confirmationPhrase === invitePhrases[command.role], {
    path: ["confirmationPhrase"],
    message: "The confirmation must name the role being invited.",
});
/**
 * Inviting a co-owner by email (stage 2, 2026-11-02). Owner access goes only to someone on the
 * reviewed owner list the release carries: a listed person is granted owner access (and a
 * pending sign-in invitation is sent again); anyone else gets a staff sign-in with no access at
 * all, whose staff reference is added to the owner list in a reviewed release before the grant.
 */
export const adminOwnerInviteCommandSchema = z
    .object({
    email: z.string().trim().toLowerCase().email().max(254),
    role: z.literal("ADMIN"),
    confirmationPhrase: z.literal("INVITE OWNER"),
})
    .strict();
export const adminOwnerInviteResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsPartnerSchemaVersionSchema,
        /** The staff reference the reviewed owner list names. */
        operatorId: z.string().min(1).max(128),
        /** Only when owner access was requested. */
        request: adminAccessRequestSchema.optional(),
        /** Whether a sign-in invitation email went out (a new one or the newest again). */
        invitationSent: z.boolean(),
        delivery: z.enum([
            // On the owner list: owner access is requested and applies within a minute.
            "OWNER_ACCESS_GRANT_REQUESTED",
            // Not on the owner list yet: a staff sign-in with no access, waiting for a release.
            "OWNER_WAITING_FOR_OWNER_LIST",
        ]),
    })
        .strict()
        .refine((data) => Boolean(data.request) === (data.delivery === "OWNER_ACCESS_GRANT_REQUESTED"), { message: "Only a requested owner grant carries its request.", path: ["request"] }),
})
    .strict();
/**
 * What an owner's access change came to (stage 2, 2026-11-02), so "Change requested" settles:
 * APPLIED, FAILED (it can't apply: a newer change landed first, or it was refused), WAITING (it
 * is still due to apply, or is waiting for a second owner), or NOT_APPLIED (it should have
 * applied by now and didn't; nothing changed).
 */
export const adminAccessRequestOutcomeSchema = z.enum([
    "APPLIED",
    "FAILED",
    "WAITING",
    "NOT_APPLIED",
]);
export const adminAccessRequestOutcomeResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsPartnerSchemaVersionSchema,
        request: adminAccessRequestSchema,
        outcome: adminAccessRequestOutcomeSchema,
        checkedAt: z.string().datetime(),
    })
        .strict(),
})
    .strict();
export const adminOwnerGrantCommandSchema = z
    .object({
    expectedRegistryVersion: z.number().int().nonnegative(),
    confirmationPhrase: z.literal("GRANT OWNER ACCESS"),
})
    .strict();
export const adminOwnerDisableCommandSchema = z
    .object({
    expectedRegistryVersion: z.number().int().positive(),
    confirmationPhrase: z.literal("DISABLE OWNER ACCESS"),
})
    .strict();
export const adminOwnerChangeResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsPartnerSchemaVersionSchema,
        request: adminAccessRequestSchema,
        delivery: z.enum(["OWNER_ACCESS_GRANT_REQUESTED", "OWNER_ACCESS_DISABLE_REQUESTED"]),
    })
        .strict(),
})
    .strict();
export const adminBusinessPartnerDisableCommandSchema = z
    .object({
    expectedRegistryVersion: z.number().int().positive(),
    role: adminInvitedRoleSchema.default("BUSINESS_PARTNER"),
    confirmationPhrase: z.enum([disablePhrases.BUSINESS_PARTNER, disablePhrases.SUPPORT_AGENT]),
})
    .strict()
    .refine((command) => command.confirmationPhrase === disablePhrases[command.role], {
    path: ["confirmationPhrase"],
    message: "The confirmation must name the role being removed.",
});
export const adminBusinessPartnerChangeResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsPartnerSchemaVersionSchema,
        /** Absent only when an invited partner who hasn't signed in yet was sent it again. */
        request: adminAccessRequestSchema.optional(),
        delivery: z.enum([
            "INVITATION_REQUESTED",
            "INVITATION_RESENT",
            "ACCESS_ENABLE_REQUESTED",
            "ACCESS_DISABLE_REQUESTED",
        ]),
    })
        .strict()
        .refine((data) => Boolean(data.request) !== (data.delivery === "INVITATION_RESENT"), {
        message: "Only a resent invitation has no access change.",
        path: ["request"],
    }),
})
    .strict();
//# sourceMappingURL=admin-operations-access.js.map